import * as React from 'react';

import { ItemGroupRecord } from '@rematter/modules/settings/types/inventoryItem';
import { DataGridColumn, LocalizedDate } from '@rematter/ui-web';

import { useTranslations } from '../../../../i18n/useTranslations';

type Props = {
  onRowClick: (row: ItemGroupRecord) => void;
};

export const useItemGroupsPanelColumns = ({ onRowClick }: Props) => {
  const t = useTranslations();

  const handleCellDoubleClick = React.useCallback(
    ({ row }: { row: ItemGroupRecord }) => {
      onRowClick(row);
    },
    [onRowClick],
  );

  const columns: DataGridColumn<ItemGroupRecord>[] = React.useMemo(
    () => [
      {
        key: 'name',
        header: t.inventoryView.itemGroupsPanel.columns.name,
        sortable: true,
        width: 200,
        onDoubleClick: handleCellDoubleClick,
        cell: ({ row }) => (
          <div className="flex min-h-8 items-center text-left font-medium">
            {row.name}
          </div>
        ),
      },
      {
        key: 'number',
        header: t.inventoryView.itemGroupsPanel.columns.number,
        sortable: true,
        width: 120,
        onDoubleClick: handleCellDoubleClick,
        cell: ({ row }) => (
          <div className="text-left font-mono">{row.number}</div>
        ),
      },
      {
        key: 'parent',
        header: t.inventoryView.itemGroupsPanel.columns.parentGroup,
        width: 180,
        onDoubleClick: handleCellDoubleClick,
        cell: ({ row }) => (
          <div className="text-left text-neutral/70">
            {row.parent?.name ?? '-'}
          </div>
        ),
      },
      {
        key: 'description',
        header: t.inventoryView.itemGroupsPanel.columns.description,
        width: 250,
        onDoubleClick: handleCellDoubleClick,
        cell: ({ row }) => (
          <div className="truncate text-left text-neutral/70">
            {row.description || '-'}
          </div>
        ),
      },
      {
        key: 'createdAt',
        header: t.inventoryView.itemGroupsPanel.columns.created,
        sortable: true,
        width: 120,
        onDoubleClick: handleCellDoubleClick,
        cell: ({ row }) => (
          <div className="text-left">
            <LocalizedDate date={row.timestamps.createdAt} />
          </div>
        ),
      },
    ],
    [t, handleCellDoubleClick],
  );

  const columnVisibilityState = React.useMemo(
    () => Object.fromEntries(columns.map((col) => [col.key, true])),
    [columns],
  );

  return { columns, columnVisibilityState };
};
